import React, { useState, useEffect, useCallback } from 'react';
import useAuth from '../hooks/useAuth';
import bookingService from '../services/bookingService';
import { formatCurrency, formatDate, calculateRefund } from '../utils/helpers';
import QRCodeDisplay from '../components/QRCodeDisplay';

const BookingsPage = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancellationModalOpen, setCancellationModalOpen] = useState(false);

  const fetchUserBookings = useCallback(async () => {
    if (!user) return;

    try {
      const userBookings = await bookingService.getUserBookings(user.uid);
      setBookings(userBookings);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      alert('Error loading bookings');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchUserBookings();
  }, [fetchUserBookings]);

  const handleCancelBooking = async (pnr) => {
    try {
      const result = await bookingService.cancelBooking(pnr);
      alert(`Booking cancelled successfully. Refund amount: ${formatCurrency(result.refund)}`);
      fetchUserBookings();
      setSelectedBooking(null);
      setCancellationModalOpen(false);
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert('Error cancelling booking: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">My Bookings</h1>

        {bookings.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <p className="text-lg text-gray-600">You haven't made any bookings yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map(booking => (
              <div
                key={booking.pnr}
                className="bg-white rounded-lg shadow hover:shadow-lg transition cursor-pointer"
                onClick={() => setSelectedBooking(selectedBooking?.pnr === booking.pnr ? null : booking)}
              >
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-sm text-gray-600">PNR: <span className="font-bold text-lg">{booking.pnr}</span></p>
                      <p className="text-sm text-gray-600">Booking Date: {formatDate(booking.bookingDate)}</p>
                    </div>
                    <span className={`px-4 py-2 rounded font-semibold text-white ${
                      booking.status === 'confirmed' ? 'bg-green-500' :
                      booking.status === 'cancelled' ? 'bg-red-500' :
                      'bg-yellow-500'
                    }`}>
                      {booking.status.toUpperCase()}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Train</p>
                      <p className="font-semibold">{booking.trainId}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Seats</p>
                      <p className="font-semibold">{booking.seats.length} seats</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Fare</p>
                      <p className="font-semibold">{formatCurrency(booking.totalFare)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Passengers</p>
                      <p className="font-semibold">{booking.passengers.length}</p>
                    </div>
                  </div>

                  {selectedBooking?.pnr === booking.pnr && (
                    <div className="mt-6 pt-6 border-t">
                      {/* Detailed View */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <h3 className="font-bold mb-3">Passengers</h3>
                          <ul className="space-y-2">
                            {booking.passengers.map((passenger, idx) => (
                              <li key={idx} className="text-sm border-l-2 border-primary pl-2">
                                {passenger.name} ({passenger.category})
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <h3 className="font-bold mb-3">Seats</h3>
                          <div className="flex flex-wrap gap-2">
                            {booking.seats.map((seat, idx) => (
                              <span key={idx} className="bg-primary text-white px-3 py-1 rounded text-sm">
                                Coach {seat.coachNumber} - Seat {seat.seatNumber}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* QR Code */}
                      {booking.status === 'confirmed' && (
                        <div className="mb-6 flex justify-center">
                          <div>
                            <p className="text-sm text-gray-600 text-center mb-2">e-Ticket QR Code</p>
                            <QRCodeDisplay data={booking.pnr} size={200} />
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      {booking.status === 'confirmed' && (
                        <div className="flex gap-3">
                          <button
                            onClick={() => setCancellationModalOpen(true)}
                            className="flex-1 bg-red-500 text-white py-2 rounded hover:bg-red-600 transition"
                          >
                            Cancel Booking
                          </button>
                          <button className="flex-1 bg-primary text-white py-2 rounded hover:bg-blue-700 transition">
                            Download Ticket
                          </button>
                        </div>
                      )}

                      {booking.status === 'cancelled' && (
                        <div className="bg-gray-100 p-4 rounded">
                          <p className="text-sm"><strong>Refund Amount:</strong> {formatCurrency(booking.refundAmount)}</p>
                          <p className="text-sm"><strong>Cancelled on:</strong> {formatDate(booking.cancellationDate)}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cancellation Modal */}
      {cancellationModalOpen && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md">
            <h2 className="text-xl font-bold mb-4">Cancel Booking?</h2>
            <p className="text-gray-600 mb-4">
              Are you sure you want to cancel PNR <strong>{selectedBooking.pnr}</strong>?
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Estimated refund: <strong>{formatCurrency(calculateRefund(selectedBooking.totalFare))}</strong>
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setCancellationModalOpen(false)}
                className="flex-1 bg-gray-200 text-gray-900 py-2 rounded hover:bg-gray-300 transition"
              >
                No, Keep It
              </button>
              <button
                onClick={() => handleCancelBooking(selectedBooking.pnr)}
                className="flex-1 bg-red-500 text-white py-2 rounded hover:bg-red-600 transition"
              >
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingsPage;
